
import { computed, defineComponent, PropType, reactive } from 'vue'
import { onBeforeMount } from 'vue'

// components
import Cheap from '@/common/components/UI/Cheap/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import TheTextarea from '@/common/components/UI/TheTextarea/index.vue'
import Modal from '@/common/components/UI/Modal/index.vue'
import ButtonDropdown from '@/common/components/UI/ButtonDropdown/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'

// types
import { RPPT } from '@/core/types/rpp.types'

// composable
import { useRouter } from 'vue-router'
import useNotifications from '@/common/composable/useNotifications'
import useAccount from '@/common/composable/useAccount'

// module components
import ChangeCompanyModal from '@/modules/RPPModule/components/ChangeCompanyModal/index.vue'
import { rpp } from '@/core/realisations/rpp.realisation'

import { formatDateFromBackend } from '@/common/helpers/formatDate'

export default defineComponent({
    components: {
        Cheap,
        TheButton,
        ButtonDropdown,
        ChangeCompanyModal,
        Modal,
        TheTextarea,
        Checkbox,
    },
    props: {
        item: {
            type: Object as PropType<RPPT>,
            required: true,
        },
        isSelected: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['select', 'unselect', 'changed', 'deleted', 'submit', 'confirm'],
    setup(props, { emit }) {
        const account = useAccount()

        const state = reactive({
            is_change: false,
            is_change_loading: false,
            is_submit: false,
            is_submit_loading: false,
            is_deleting: false,
            is_head_of_transport_department: false,
            comment: null as string | null,
            statusCode: null as string | null,
        })

        const router = useRouter()
        const notifications = useNotifications()
        const { havePermissions } = useAccount()

        const onEditRPP = () => {
            router.push(`/contracts/${props.item.company_id}`)
        }

        const getFullPath = () => {
            if (!props.item) return

            let stocks = [...props.item.stock_delivery_notes]

            let full_path = stocks
                .sort(
                    (stock_one, stock_two) => stock_one.receiver_number - stock_two.receiver_number
                )
                .map((stock) => stock.city_name)
                .join(' - ')
            full_path = props.item.sender_city_name + ' - ' + full_path

            return full_path || null
        }

        const toggleChange = () => (state.is_change = !state.is_change)

        const changeCompany = async (companyId: number) => {
            try {
                state.is_change_loading = true

                await rpp.changeCompany(props.item.id, companyId)

                notifications.notify({ title: 'Компания успешно изменена', type: 'success' })
                toggleChange()
                emit('changed')
            } catch (error) {
                console.error(error)
                notifications.notifyError({ error, title: 'Ошибка при изменении компании' })
            } finally {
                state.is_change_loading = false
            }
        }

        const getCostPriceTitle = computed(() =>
            props.item && props.item.with_nds
                ? 'Себестоимость (с НДС):'
                : 'Себестоимость (без НДС):'
        )

        const onDelete = async () => {
            if (state.is_deleting) return

            const answer = confirm(`Вы действительно хотите удалить РПП (id: ${props.item.id}) ?`)
            if (!answer) return

            try {
                state.is_deleting = true

                await rpp.deleteRpp(props.item.id)

                notifications.notify({
                    type: 'success',
                    title: `РПП (id: ${props.item.id}) удален`,
                })

                emit('deleted')
            } catch (error) {
                console.error(error)

                notifications.notifyError({
                    error,
                    title: `При удалении РПП (id: ${props.item.id}) произошла ошибка`,
                })
            } finally {
                state.is_deleting = false
            }
        }

        const isOnInspectionHeadTransportStatus = computed(() => {
            // 'На проверке у РТО'
            return props.item && props.item.status_id === 11
        })

        const toggleChangeStatus = (statusCode?: string) => {
            state.comment = null
            state.is_submit = !state.is_submit
            state.statusCode = statusCode || null
        }

        const submitRpp = async () => {
            if (!props.item.id || !state.statusCode) return

            try {
                state.is_submit_loading = true

                const payload = {
                    statusCode: state.statusCode,
                    comment: state.comment || '',
                    paymentBySupplierIds: [props.item.id],
                }

                await rpp.submitRpp(payload)

                const notifyStr = state.statusCode === 'confirmed' ? 'подтвержден' : 'отказан'
                notifications.notify({
                    title: `РПП успешно ${notifyStr}!`,
                    type: 'success',
                })
                toggleChangeStatus()
                emit('submit')
            } catch (error) {
                console.error(error)
                notifications.notifyError({
                    error,
                    title: 'Произошла ошибка',
                })
            } finally {
                state.is_submit_loading = false
            }
        }

        onBeforeMount(() => {
            state.is_head_of_transport_department = account.haveRole(22)
        })

        const onSelect = () => {
            if (props.isSelected) {
                emit('unselect', props.item)
                return
            }

            emit('select', props.item)
        }

        const onConfirm = () => {
            emit('confirm', props.item)
        }

        const formatDate = (dateStr: string) => {
            if (!dateStr) return '-'

            const dateFirst = dateStr.split(' ')[0]
            return dateFirst.split('-').reverse().join('.')
        }

        return {
            state,
            onEditRPP,
            getFullPath,
            toggleChange,
            changeCompany,
            getCostPriceTitle,
            onDelete,
            havePermissions,
            isOnInspectionHeadTransportStatus,
            submitRpp,
            toggleChangeStatus,
            onSelect,
            onConfirm,
            formatDate,
            formatDateFromBackend,
        }
    },
})
